import { transformMovementOrder } from '@/common/dto/catalog';
import {
  IGetMenhirMovementsDataEnvelope,
  IGetMovementsDataEnvelope,
  IMenhirMovement,
  IMenhirReconciliationMovement,
  IMovement,
  IReconciliationMovement,
} from '@/common/integrations/menhir/employee/movements/movements.types';
import {
  transformGenericPendingOrder,
  transformRetirementInvestmentPendingOrder,
} from '@/common/integrations/menhir/employee/orders/orders.dto';
import {
  TMenhirGenericPendingOrder,
  TMenhirRetirementInvestmentPendingOrder,
} from '@/common/integrations/menhir/employee/orders/orders.types';
import { transformVoucher } from '@/common/integrations/menhir/employee/vouchers/vouchers.dto';
import { IMenhirVoucher } from '@/common/integrations/menhir/employee/vouchers/vouchers.types';
import { IMenhirPocket } from '../pockets/pockets.types';

const transformReconciliationMovement = (
  menhirReconciliationMovement: IMenhirReconciliationMovement
): IReconciliationMovement => ({
  type: menhirReconciliationMovement.type,
});

const transformMovementPocket = (
  menhirPocket: Omit<IMenhirPocket, 'balance'>
) => ({
  id: menhirPocket.id,
  hasMovements: menhirPocket.has_movements,
  ownerId: menhirPocket.owner_id,
  ownerType: menhirPocket.owner_type,
  providerId: menhirPocket.provider_id,
  type: menhirPocket.type,
});

export const transformPendingOrder = (
  menhirPendingOrder:
    | IMenhirVoucher
    | TMenhirRetirementInvestmentPendingOrder
    | TMenhirGenericPendingOrder
) => {
  if ('beneficiary' in menhirPendingOrder) {
    return transformVoucher(menhirPendingOrder);
  } else if ('product_name' in menhirPendingOrder) {
    return transformRetirementInvestmentPendingOrder(menhirPendingOrder);
  } else {
    return transformGenericPendingOrder(menhirPendingOrder);
  }
};

export const transformMovement = (
  menhirMovement: IMenhirMovement
): IMovement => ({
  type: menhirMovement.type,
  status: menhirMovement.status,
  pocket: transformMovementPocket(menhirMovement.pocket),
  merchantName: menhirMovement.merchant_name?.toLowerCase(),
  isDebit: menhirMovement.is_debit,
  id: menhirMovement.id,
  executedAt: menhirMovement.executed_at,
  description: menhirMovement.description,
  category: menhirMovement.category,
  bankAccount: menhirMovement.bank_account,
  balanceBefore: menhirMovement.balance_before,
  balanceAfter: menhirMovement.balance_after,
  amount: menhirMovement.amount,
  voucher: menhirMovement.voucher
    ? transformVoucher(menhirMovement.voucher)
    : undefined,
  order: menhirMovement.order
    ? transformMovementOrder(menhirMovement.order)
    : undefined,
  subcategory: menhirMovement.subcategory,
  reconciliationMovement: menhirMovement.reconciliation_movement
    ? transformReconciliationMovement(menhirMovement.reconciliation_movement)
    : undefined,
  budgetEmployee: menhirMovement.budget_employee
    ? {
        budget: {
          name: menhirMovement.budget_employee.budget.name,
          logo: menhirMovement.budget_employee.budget.logo,
          id: menhirMovement.budget_employee.budget.id,
        },
        displayName: menhirMovement.budget_employee.display_name,
        id: menhirMovement.budget_employee.id,
      }
    : undefined,
  budgetExpense: menhirMovement.budget_expense,
  isTransferAdjustment: menhirMovement.is_transfer_adjustment,
});

export const transformMovementList = (
  payload: IGetMenhirMovementsDataEnvelope
): IGetMovementsDataEnvelope => ({
  movements: {
    totalResults: payload.movements.total_results,
    page: payload.movements.current_page,
    perPage: payload.movements.results_per_page,
    totalPages: payload.movements.total_pages,
    list: payload.movements.list.map(transformMovement),
  },
  pendingOrders: payload.pending_orders?.map(transformPendingOrder),
});
